import { useAuth } from "@/context/auth-context";
import { useApi } from "@/hooks/useApi";
import { useToast } from "@/hooks/useToast";
import { ScrollBar, ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { useEffect, useState } from "react";

interface Episode {
	order: number;
	title: string;
	duration: number;
	cutinScore?: number | null;
	totalScore?: number | null;
}

interface Chapter {
	order: number;
	title: string;
	episodes: Episode[];
}

interface RatingSeriesProps {
	contentId: string;
	disabled: boolean;
	episodeScores: any[];
	chapters: Chapter[];
	refetch: any;
	setShowScore: any;
	setScoreSeason: any;
}

const RatingSeries = ({
	contentId,
	disabled,
	chapters,
	refetch,
	episodeScores,
	setShowScore,
	setScoreSeason,
}: RatingSeriesProps) => {
	const { user } = useAuth();
	const { addToast, removeLoading } = useToast();
	const { call: submitUserScore, loading } = useApi();

	const scoreColors = {
		"فوق العاده": "bg-green-600",
		عالی: "bg-green-500",
		خوب: "bg-cyan-400",
		متوسط: "bg-yellow-400",
		بد: "bg-pink-500",
		افتضاح: "bg-red-500",
	};

	const getScoreColorClass = (score: number) => {
		if (score == 10) return scoreColors["فوق العاده"];
		if (score >= 9) return scoreColors["عالی"];
		if (score >= 7) return scoreColors["خوب"];
		if (score >= 5.0) return scoreColors["متوسط"];
		if (score >= 3.0) return scoreColors["بد"];
		return scoreColors["افتضاح"];
	};

	useEffect(() => {
		if (loading) {
			addToast("در حال بارگذاری...", "loading");
		} else {
			removeLoading();
		}
	}, [loading]);

	const handleScoreChange = async (
		seasonOrder: number,
		episodeOrder: number,
		score: number
	) => {
		if (!user) {
			addToast("برای امتیازدهی باید وارد شوید.", "error");
			return;
		}

		try {
			const response = await submitUserScore("/api/score/episode/submit", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					contentId,
					score,
					episodeOrder,
					seasonOrder,
				}),
			});

			if (!response.success) {
				console.error("❌ Failed to submit score");
			} else {
				await refetch();
				setScoreSeason({
					episode: episodeOrder,
					season: seasonOrder,
					score: score,
				});
				setShowScore(true);
				addToast("امتیاز ثبت شد", "success");
			}
		} catch (error) {
			console.error("Error submitting score:", error);
		}
	};

	if (!chapters || chapters.length == 0) {
		return null;
	}

	return (
		<div className="text-white p-4 lg:p-6 rtl w-full" dir="rtl">
			{chapters.map((chapter) => (
				<div key={chapter.order} className="mb-8">
					<h2 className="text-xl font-bold mb-4 text-right">{chapter.title}</h2>
					<div className="flex items-start flex-wrap gap-6 mb-4">
						{chapter.episodes.map((episode) => (
							<div key={episode.order} className="relative">
								<div className="w-max py-2 px-2 bg-dark-blue rounded-md flex items-center justify-between">
									<div className="flex gap-1 max-w-[200px] overflow-hidden">
										<span>{episode.order}</span>
										<span
											className="truncate"
											dir={
												/[\u0600-\u06FF]/.test(episode.title) ? "rtl" : "ltr"
											}
										>
											{episode.title}
										</span>
										<span className="text-gray-600 flex-shrink-0">|</span>
									</div>
									<select
										className="w-[30px] text-white bg-transparent outline-none"
										value={
											episodeScores?.find(
												(s) =>
													s.episodeOrder === episode.order &&
													s.seasonOrder === chapter.order
											)?.score ?? ""
										}
										onChange={(e) =>
											handleScoreChange(
												chapter.order,
												episode.order,
												parseFloat(e.target.value)
											)
										}
										disabled={loading || disabled}
									>
										<option
											className="bg-black"
											value=""
											disabled
											dir="ltr"
										></option>
										{Array.from({ length: 11 }, (_, i) => (
											<option
												className="bg-black text-center"
												key={i}
												value={i}
												dir="ltr"
											>
												{i}
											</option>
										))}
									</select>
								</div>
							</div>
						))}
					</div>
				</div>
			))}

			<div className="flex flex-wrap items-center justify-start gap-2 mb-6">
				{Object.entries(scoreColors).map(([label, colorClass]) => (
					<div key={label} className="flex items-center gap-2 ml-4">
						<div className={`w-4 h-4 rounded-full ${colorClass}`}></div>
						<span>{label}</span>
					</div>
				))}
			</div>
			<ScrollArea className="w-full whitespace-nowrap pb-2" dir="rtl">
				<table className="w-full border-collapse">
					<thead>
						<tr className="text-center">
							<th className="py-2 px-2 text-nowrap"></th>
							{Array.from(
								{ length: Math.max(...chapters.map((c) => c.episodes.length)) },
								(_, i) => i + 1
							).map((episodeNum) => (
								<th
									key={`episode-${episodeNum}`}
									className="py-2 px-2 text-nowrap"
								>
									قسمت {episodeNum}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{chapters.map((chapter) => (
							<tr key={`chapter-row-${chapter.order}`} className="text-center">
								<th className="py-2 px-4 text-right text-nowrap">
									{chapter.title}
								</th>
								{Array.from(
									{
										length: Math.max(...chapters.map((c) => c.episodes.length)),
									},
									(_, i) => i + 1
								).map((episodeNum) => {
									const episode = chapter.episodes.find(
										(e) => e.order === episodeNum
									);
									if (!episode)
										return (
											<td key={`${chapter.order}-empty-${episodeNum}`}></td>
										);

									const score = episode.cutinScore ?? 0;

									return (
										<td
											key={`${chapter.order}-${episode.order}`}
											className="p-1"
										>
											<div
												className={`${getScoreColorClass(
													score
												)} rounded py-2 px-4`}
											>
												{score.toFixed(1)}
											</div>
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
				<ScrollBar
					orientation="horizontal"
					className="w-full border-none"
					dir="rtl"
				/>
			</ScrollArea>
		</div>
	);
};

export default RatingSeries;
